import React, { useState, useEffect } from 'react';
import { frontendDb as db } from '../../lib/frontend-db';
import type { ContactFormSettings, ContactFormRequest } from '../../lib/frontend-db';

const ContactFormPage = () => {
  const [activeTab, setActiveTab] = useState<'content' | 'styling' | 'requests'>('content');
  const [settings, setSettings] = useState<ContactFormSettings | null>(null);
  const [requests, setRequests] = useState<ContactFormRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      let data: ContactFormSettings | null = null;
      
      try {
        data = await db.getContactFormSettings() as ContactFormSettings | null;
      } catch (error: any) {
        // Wenn Tabelle nicht existiert, verwende Standard-Werte
        if (error?.message?.includes('doesn\'t exist')) {
          console.warn('⚠️ contact_form_settings Tabelle existiert noch nicht. Bitte führen Sie die Migration aus.');
          setMessage('⚠️ Bitte führen Sie zuerst die Datenbank-Migration aus: mysql-migrations/20250112_stimmart_rebranding.sql');
        } else {
          throw error;
        }
      }
      
      setSettings(data || {
        id: 'default-contact-form',
        enabled: true,
        section_title: 'Kontaktformular',
        form_title: 'Schreiben Sie uns',
        form_description: 'Wir freuen uns auf Ihre Nachricht!',
        name_label: 'Name',
        email_label: 'E-Mail',
        field_type: 'subject',
        subject_label: 'Betreff',
        number_label: 'Anzahl Personen',
        message_label: 'Nachricht',
        submit_button_text: 'Absenden',
        success_message: 'Vielen Dank für Ihre Nachricht! Wir werden uns schnellstmöglich bei Ihnen melden.',
        error_message: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
        background_color: 'transparent',
        text_color: 'inherit',
        created_at: '',
        updated_at: ''
      });

      if (activeTab === 'requests') {
        try {
          const requestsData = await db.getContactFormRequests() as ContactFormRequest[];
          setRequests(requestsData);
        } catch (error: any) {
          if (!error?.message?.includes('doesn\'t exist')) {
            console.error('Error loading requests:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    setSaving(true);
    setMessage('');

    try {
      await db.updateContactFormSettings(settings);
      setMessage('✅ Einstellungen erfolgreich gespeichert!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('❌ Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof ContactFormSettings, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return <div className="p-8">Fehler beim Laden der Einstellungen</div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Kontaktformular</h2>
        <p className="text-gray-600">
          Verwalten Sie das Kontaktformular und sehen Sie eingegangene Anfragen.
        </p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('✅') 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        {/* Enable/Disable Toggle */}
        <div className="p-6 bg-blue-50 rounded-t-lg flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Kontaktformular aktivieren</h3>
            <p className="text-sm text-gray-600">Zeigt das Formular auf der Startseite an</p>
          </div>
          <button
            onClick={() => updateSetting('enabled', !settings.enabled)}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
              settings.enabled ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                settings.enabled ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              { id: 'content', label: 'Inhalte' },
              { id: 'styling', label: 'Styling' },
              { id: 'requests', label: 'Anfragen' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as any);
                  if (tab.id === 'requests') {
                    loadData();
                  }
                }}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'content' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sektions-Titel
                </label>
                <input
                  type="text"
                  value={settings.section_title}
                  onChange={(e) => updateSetting('section_title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formular-Titel
                </label>
                <input
                  type="text"
                  value={settings.form_title}
                  onChange={(e) => updateSetting('form_title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Beschreibung
                </label>
                <textarea
                  value={settings.form_description}
                  onChange={(e) => updateSetting('form_description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name-Label
                  </label>
                  <input
                    type="text"
                    value={settings.name_label}
                    onChange={(e) => updateSetting('name_label', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail-Label
                  </label>
                  <input
                    type="text"
                    value={settings.email_label}
                    onChange={(e) => updateSetting('email_label', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Feldtyp-Auswahl */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  3. Feld-Typ (zwischen Name und Nachricht)
                </label>
                <div className="flex gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => updateSetting('field_type', 'subject')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-colors ${
                      settings.field_type === 'subject'
                        ? 'border-purple-500 bg-purple-100 text-purple-800'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-purple-300'
                    }`}
                  >
                    📝 Betreff (Textfeld)
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSetting('field_type', 'number')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-colors ${
                      settings.field_type === 'number'
                        ? 'border-purple-500 bg-purple-100 text-purple-800'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-purple-300'
                    }`}
                  >
                    🔢 Anzahl (Dropdown 1-20)
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Betreff-Label
                    </label>
                    <input
                      type="text"
                      value={settings.subject_label}
                      onChange={(e) => updateSetting('subject_label', e.target.value)}
                      disabled={settings.field_type !== 'subject'}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        settings.field_type !== 'subject' ? 'bg-gray-100 text-gray-500' : ''
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Anzahl-Label
                    </label>
                    <input
                      type="text"
                      value={settings.number_label}
                      onChange={(e) => updateSetting('number_label', e.target.value)}
                      disabled={settings.field_type !== 'number'}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        settings.field_type !== 'number' ? 'bg-gray-100 text-gray-500' : ''
                      }`}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nachricht-Label
                </label>
                <input
                  type="text"
                  value={settings.message_label}
                  onChange={(e) => updateSetting('message_label', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button-Text
                </label>
                <input
                  type="text"
                  value={settings.submit_button_text}
                  onChange={(e) => updateSetting('submit_button_text', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Erfolgsmeldung
                </label>
                <textarea
                  value={settings.success_message}
                  onChange={(e) => updateSetting('success_message', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fehlermeldung
                </label>
                <textarea
                  value={settings.error_message}
                  onChange={(e) => updateSetting('error_message', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {activeTab === 'styling' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hintergrundfarbe
                </label>
                <div className="flex gap-4">
                  <input
                    type="color"
                    value={settings.background_color === 'transparent' ? '#ffffff' : settings.background_color}
                    onChange={(e) => updateSetting('background_color', e.target.value)}
                    className="h-12 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.background_color}
                    onChange={(e) => updateSetting('background_color', e.target.value)}
                    placeholder="z.B. #ffffff oder transparent"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Verwenden Sie "transparent" für automatische Dark Mode Anpassung
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Textfarbe
                </label>
                <div className="flex gap-4">
                  <input
                    type="color"
                    value={settings.text_color === 'inherit' ? '#000000' : settings.text_color}
                    onChange={(e) => updateSetting('text_color', e.target.value)}
                    className="h-12 w-20 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={settings.text_color}
                    onChange={(e) => updateSetting('text_color', e.target.value)}
                    placeholder="z.B. #000000 oder inherit"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Verwenden Sie "inherit" für automatische Dark Mode Anpassung
                </p>
              </div>
            </div>
          )}

          {activeTab === 'requests' && (
            <div>
              <div className="mb-4 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">
                  Eingegangene Anfragen ({requests.length})
                </h3>
                <button
                  onClick={loadData}
                  className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Aktualisieren
                </button>
              </div>

              {requests.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  Noch keine Anfragen eingegangen
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          E-Mail
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Betreff / Anzahl
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Datum
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {requests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{request.request_id.toString().padStart(5, '0')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {request.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <a href={`mailto:${request.email}`} className="text-blue-600 hover:underline">
                              {request.email}
                            </a>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {request.subject || (request.number_value ? `${request.number_value} Personen` : '-')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(request.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save Button (nur für content und styling tabs) */}
        {activeTab !== 'requests' && (
          <div className="px-6 pb-6 flex justify-end border-t pt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Wird gespeichert...' : 'Änderungen speichern'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactFormPage;


